import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid2 as Grid,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { pipeline_tree } from "../../data/city_sites/permitTypesTree";
import { getAllPermitsInPackage } from "../permitForms/permitFormHelpers";
import { updatePermitPackage } from "../../store/actions/dataActions";
import { setSelectedRow } from "../../store/actions/uiActions/myPermitsActions";

// EXACT same function as in RenderCategoryBinds.js
const getOptions = (stepKey, miniMap) => {
  try {
    const permit = pipeline_tree.find((p) => p.id === miniMap[0]);
    if (!permit) return [];

    if (stepKey === "ProposedUseId") {
      return permit.proposed_use || [];
    }

    const proposed = (permit.proposed_use || []).find(
      (p) => p.id === miniMap[1]
    );
    if (!proposed) return [];

    if (stepKey === "StructureTypeId") {
      return proposed.structure_type || [];
    }

    const structure = (proposed.structure_type || []).find(
      (s) => s.id === miniMap[2]
    );
    if (!structure) return [];

    if (stepKey === "WorkTypeId") {
      return structure.work_type || [];
    }

    const work = (structure.work_type || []).find((w) => w.id === miniMap[3]);
    if (!work) return [];

    if (stepKey === "WorkSubTypeId") {
      return work.work_sub_type || [];
    }

    return [];
  } catch (err) {
    console.error("getOptions error", err);
    return [];
  }
};

// EXACT same function as in RenderCategoryBinds.js
const formatLabel = (str) => {
  return str
    .replace(/_/g, " ") // convert underscores to spaces
    .replace(/(^|[/\s])([a-zA-Z])/g, (match, p1, p2) => p1 + p2.toUpperCase());
};

const AdditionalPermitCitySetupForm = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const selectedRow = useSelector(
    (state) => state.myPermitsComponent.selectedRow
  );
  const selectedPermitType = useSelector(
    (state) => state.myPermitsComponent.selectedPermitType
  );

  // Get preset address from the existing permit package
  const primaryAddress = selectedRow?.project_address || "";

  // Get the permit type ID from the additional permit's connect_to_city object
  const additionalPermitConnectToCity =
    selectedRow?.additional_permits?.[selectedPermitType]?.connect_to_city;
  const initialPermitTypeId =
    additionalPermitConnectToCity?.permit_type_dict?.id || 1;

  // Get main permit's base_permit_map to preset Proposed Use and Structure Type
  const mainPermitBaseMap = selectedRow?.connect_to_city?.base_permit_map || [];
  const mainProposedUse = mainPermitBaseMap[1]; // Second element is ProposedUseId
  const mainStructureType = mainPermitBaseMap[2]; // Third element is StructureTypeId

  // EXACT same steps definition as RenderCategoryBinds
  const steps = useMemo(
    () => [
      { key: "ProposedUseId", label: "Proposed Use" },
      { key: "StructureTypeId", label: "Structure Type" },
      { key: "WorkTypeId", label: "Work Type" },
      { key: "WorkSubTypeId", label: "Work Sub Type" },
    ],
    []
  );

  // Initialize with preset values from main permit
  const initialFormSelections = useMemo(
    () => ({
      ProposedUseId: mainProposedUse || undefined,
      StructureTypeId: mainStructureType || undefined,
      WorkTypeId: undefined,
      WorkSubTypeId: undefined,
    }),
    [mainProposedUse, mainStructureType]
  );

  // Calculate initial miniMap including preset values
  const initialMiniMap = useMemo(() => {
    const map = [initialPermitTypeId];
    if (mainProposedUse) map.push(mainProposedUse);
    if (mainStructureType) map.push(mainStructureType);
    return map;
  }, [initialPermitTypeId, mainProposedUse, mainStructureType]);

  const [miniMap, setMiniMap] = useState(initialMiniMap);
  const [formSelections, setFormSelections] = useState(initialFormSelections);
  const [completePermitLoading, setCompletePermitLoading] = useState(false);

  // EXACT same handleChange function as RenderCategoryBinds
  const handleChange = (step, value) => {
    const stepIndex = steps.findIndex((s) => s.key === step);
    const newFormSelections = { ...formSelections };

    // Set current step value, clear all later steps
    steps.forEach(({ key }, i) => {
      if (i < stepIndex) return;
      newFormSelections[key] = i === stepIndex ? value : undefined;
    });

    // Recalculate miniMap based on valid steps
    const newMiniMap = [initialPermitTypeId];

    steps.slice(0, stepIndex + 1).forEach(({ key }) => {
      const val = newFormSelections[key];
      if (val !== undefined) {
        newMiniMap.push(...(Array.isArray(val) ? val : [val]));
      }
    });

    // ✅ Prune future steps if their options are no longer valid
    steps.slice(stepIndex + 1).forEach(({ key }) => {
      const nextOptions = getOptions(key, newMiniMap);
      const currentVal = newFormSelections[key];

      if (
        !nextOptions.some((opt) => {
          return Array.isArray(currentVal)
            ? currentVal.includes(opt.id)
            : opt.id === currentVal;
        })
      ) {
        newFormSelections[key] = undefined;
      }
    });

    setFormSelections(newFormSelections);
    setMiniMap(newMiniMap);
  };

  // EXACT same validation logic as RenderCategoryBinds
  const allStepsCompleted = steps.every(({ key }) => {
    const options = getOptions(key, miniMap);
    if (!options.length) return true; // step not needed
    const val = formSelections[key];
    return (
      val !== undefined &&
      val !== "" &&
      (Array.isArray(val) ? val.length > 0 : true)
    );
  });

  const isFormValid = useMemo(() => allStepsCompleted, [allStepsCompleted]);

  const allPermits = getAllPermitsInPackage(selectedRow, selectedPermitType);

  const dispatch = useDispatch();

  const handleSubmit = async () => {
    try {
      setCompletePermitLoading(true);
      // Get the actual labels for the selected IDs using EXACT same logic as RenderCategoryBinds
      const getLabelsForStep = (stepKey, value) => {
        const options = getOptions(stepKey, miniMap);
        if (Array.isArray(value)) {
          return value
            .map((val) => {
              const found = options.find((opt) => opt.id === val);
              return found ? formatLabel(found.name) : "";
            })
            .filter(Boolean);
        } else {
          const found = options.find((opt) => opt.id === value);
          return found ? formatLabel(found.name) : "";
        }
      };

      const citySetupData = {
        formSelections,
        miniMap,
        labels: {
          proposedUse: getLabelsForStep(
            "ProposedUseId",
            formSelections.ProposedUseId
          ),
          structureType: getLabelsForStep(
            "StructureTypeId",
            formSelections.StructureTypeId
          ),
          workType: getLabelsForStep("WorkTypeId", formSelections.WorkTypeId),
          workSubType: Array.isArray(formSelections.WorkSubTypeId)
            ? getLabelsForStep(
                "WorkSubTypeId",
                formSelections.WorkSubTypeId
              ).join(", ")
            : getLabelsForStep("WorkSubTypeId", formSelections.WorkSubTypeId),
        },
        allPermitsInPackage: allPermits,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/additional-permit/update-city-setup`,
        {
          task_id: selectedRow?.task_id,
          selectedPermitType: selectedPermitType,
          citySetupData: citySetupData,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
          timeout: 120000, // 2 minutes timeout
        }
      );

      if (response?.data?.success) {
        const responseData = response?.data?.data;

        // The backend returns the updated package in responseData.updated_package
        const updatedPackage = responseData.updated_package;

        if (updatedPackage) {
          await dispatch(updatePermitPackage(updatedPackage));
          await dispatch(setSelectedRow(updatedPackage));
        } else {
          console.error("⚠️ No updated_package in response data");
        }

        // Show success message with any warnings
        const permitNumber = responseData.permit_number;
        const linkFetchSuccess = responseData.link_fetch_success;

        if (!linkFetchSuccess) {
          alert(
            `🎉 Additional permit created successfully!\n\nPermit Number: ${permitNumber}\n\n⚠️ Note: The permit link could not be fetched immediately, but your permit is available in the JAXepics dashboard.`
          );
        }

        // Could dispatch an action to refresh the selectedRow data
        // window.location.reload(); // Uncomment if you want to refresh the page
      } else {
        console.error("Failed to save city setup");
        alert("❌ Failed to complete city setup. Please try again.");
      }
    } catch (error) {
      console.error("Error saving city setup:", error);

      // Handle different types of errors
      let errorMessage = "An unexpected error occurred";
      let userMessage = "❌ Error: An unexpected error occurred";

      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        errorMessage = "Request timeout - the operation took too long";
        userMessage =
          "⏰ Request Timeout: The permit creation is taking longer than expected. Please check your JAXepics dashboard to see if the permit was created successfully.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        userMessage = `❌ Error: ${errorMessage}`;
      } else if (error.message) {
        errorMessage = error.message;
        userMessage = `❌ Error: ${errorMessage}`;
      }

      // Show user-friendly error message
      alert(userMessage);

      // If it's a specific error about main permit connection, show helpful message
      if (errorMessage.includes("Main permit must be connected")) {
        alert(
          "⚠️ Your main permit must be connected to the city first before creating additional permits."
        );
      }
    } finally {
      setCompletePermitLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, pt: 1, maxWidth: "800px", mx: "auto" }}>
      {/* Alert Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This additional permit will use the same address and property as your
          main permit. Complete the fields below to generate the permit on the
          city site.
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", gap: 1 }}>
        {/* Address Field (Preset and Locked) */}
        <TextField
          fullWidth
          label="Property Address"
          value={primaryAddress}
          disabled
          variant="outlined"
          helperText="Address is preset from your main permit and cannot be changed"
          sx={{
            mb: 3,
            "& .MuiInputBase-input": {
              color: "text.primary",
            },
            "& .MuiInputLabel-root": {
              color: "text.primary",
            },
          }}
        />
        {/* Complete Setup Button */}
        <Box>
          <LoadingButton
            loading={completePermitLoading}
            variant="contained"
            size="large"
            disabled={!isFormValid}
            sx={{
              minWidth: "200px",
              py: 1.5,
              fontWeight: "bold",
            }}
            onClick={handleSubmit}
          >
            Complete City Setup
          </LoadingButton>
        </Box>
      </Box>

      {/* City Setup Fields - EXACT same pattern as RenderCategoryBinds */}
      <Grid container spacing={1}>
        {steps.map((stepObj, index) => {
          const prevStepFilled =
            index === 0 || formSelections[steps[index - 1].key] !== undefined;
          const step = stepObj.key;
          const label = stepObj.label;

          // Preset fields (Proposed Use and Structure Type) should be disabled/locked
          const isPresetField =
            step === "ProposedUseId" || step === "StructureTypeId";
          const isDisabled = isPresetField || !prevStepFilled;

          const options = getOptions(step, miniMap);

          if (!options.length) return null;

          const isMulti = step === "WorkSubTypeId"; // explicitly define it
          const value = formSelections[step] || (isMulti ? [] : "");

          // Don't render preset fields at all - they're hidden anyway
          if (isPresetField) return null;

          return (
            <Grid size={{ xs: 12, md: 6 }} key={step}>
              <FormControl
                variant="outlined"
                fullWidth
                disabled={isDisabled}
                sx={{
                  // Fix label cutoff issue with proper background and positioning
                  "& .MuiInputLabel-root": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    zIndex: 1,
                  },
                  // Ensure focused and filled labels have proper background
                  "& .MuiInputLabel-root.Mui-focused": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    zIndex: 2,
                  },
                  "& .MuiInputLabel-root.MuiFormLabel-filled": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    zIndex: 2,
                  },
                  // Target the shrunk label specifically
                  "& .MuiInputLabel-shrink": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    zIndex: 2,
                  },
                }}
              >
                <InputLabel id={`${step}-label`}>{label}</InputLabel>
                <Select
                  labelId={`${step}-label`}
                  multiple={isMulti}
                  value={value}
                  onChange={(e) => handleChange(step, e.target.value)}
                  label={label}
                  renderValue={(selected) => {
                    const selectedArray = isMulti ? selected : [selected];
                    const labels = selectedArray
                      .map((val) => {
                        const found = options.find((opt) => opt.id === val);
                        return formatLabel(found?.name || val);
                      })
                      .filter(Boolean);
                    return isMulti ? labels.join(", ") : labels[0];
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        marginTop: "1.5px",
                        maxHeight: 190, // Set the desired max height for the dropdown menu
                        width: "auto",
                        maxWidth: "100%",
                      },
                    },
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {isMulti && (
                        <Checkbox checked={value.includes(option.id)} />
                      )}
                      <ListItemText primary={formatLabel(option.name)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 6, borderTop: "2px solid #f3f3f3", pt: 2 }}>
        {/* Associate Permits Grid */}
        <Typography variant="h6" sx={{ mb: 0.5, ml: 1 }}>
          Associate Permits
        </Typography>

        <Box sx={{ height: 300, width: "100%" }}>
          <DataGrid
            rows={allPermits.filter((p) => !p.isBase)}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5, 10]}
            columns={[
              {
                field: "name",
                headerName: "Permit Type",
                flex: 1,
                renderCell: (params) => (
                  <Box
                    sx={{
                      backgroundColor: params.row.isCurrent
                        ? "#e3f2fd"
                        : "transparent",
                      padding: "4px 8px",

                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={params.row.isCurrent ? "bold" : "normal"}
                      color={
                        params.row.isCurrent ? "primary.main" : "text.primary"
                      }
                    >
                      {params.value}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: "permitNumber",
                headerName: "Permit Number",
                flex: 1,
                renderCell: (params) => (
                  <Box
                    sx={{
                      backgroundColor: params.row.isCurrent
                        ? "#e3f2fd"
                        : "transparent",
                      padding: "4px 8px",

                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      color={
                        params.row.isCurrent ? "primary.main" : "text.primary"
                      }
                      fontWeight={params.row.isCurrent ? "bold" : "normal"}
                    >
                      {params.value}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: "status",
                headerName: "Status",
                width: 120,
                renderCell: (params) => (
                  <Box
                    sx={{
                      backgroundColor: params.row.isCurrent
                        ? "#e3f2fd"
                        : "transparent",
                      padding: "4px 8px",

                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      color={
                        params.row.isCurrent ? "primary.main" : "text.primary"
                      }
                      fontWeight={params.row.isCurrent ? "bold" : "normal"}
                    >
                      {params?.row?.isCurrent ? "New" : "Associated"}
                    </Typography>
                  </Box>
                ),
              },
            ]}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            getRowHeight={() => 40}
            //   hideFooter
            sx={{
              border: "1px solid #FCF2F0",
              "& .MuiDataGrid-main": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-cell": {
                border: "none !important",
                padding: "0px 0px",
              },
              "& .MuiDataGrid-row": {
                border: "none !important",
                minHeight: "40px !important",
                "&:hover": {
                  backgroundColor: "transparent !important",
                },
                "&.Mui-selected": {
                  backgroundColor: "transparent !important",
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid #555",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:hover": {
                  ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                    {
                      visibility: "hidden",
                      pointerEvents: "none",
                    },
                  cursor: "default",
                  backgroundColor: "#333 !important",
                },
                backgroundColor: "#333",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
              },
              "& .MuiDataGrid-columnHeader--sortable": {
                pointerEvents: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #ccc",
              },
            }}
          />
        </Box>
      </Box>

      {/* Additional Info */}
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> This process will not create a new NOC as you
          already have one for this property. The address and property details
          will remain the same as your main permit.
        </Typography>
      </Alert>
    </Box>
  );
};

export default AdditionalPermitCitySetupForm;
